import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { VideoTestimonialCard } from "./eventCards";
import WrittenReviewCard from "./WrittenReviewCard";
import "./HomeTestimonialsSection.css";
import * as myConstClass from "../constant";
import { getLanguage } from 'react-switch-lang';
import en from '../en.json';
import ma from '../ma.json';

const translationData = { en: en, mr: ma };

// Extract YouTube video ID from various YouTube URL formats
const extractYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/.*[?&]v=([^&\n?#]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Get YouTube thumbnail from URL, using the same logic as OnlineLearningSection (hqdefault)
const getYouTubeThumbnail = (url) => {
  const id = extractYouTubeVideoId(url);
  if (!id) return "";
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
};

const HomeTestimonialsSection = ({
  userId,
  className = "",
  ...rest
}) => {


  const [videos, setVideos] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [debugLogs, setDebugLogs] = useState([]);
  const addLog = (msg) => setDebugLogs(prev => [...prev, msg]);
  const [language, setLanguage] = useState(() => getLanguage() || 'en');
  const t = (key) => {
    const langKey = language === 'ma' || language === 'mr' ? 'mr' : 'en';
    return translationData[langKey]?.[key] || '';
  };

  // Listen for language changes via polling (same-tab) and storage event (cross-tab)
  useEffect(() => {
    const checkLang = () => {
      const current = getLanguage() || 'en';
      setLanguage(prev => prev !== current ? current : prev);
    };
    checkLang();
    const interval = setInterval(checkLang, 500);
    window.addEventListener("storage", checkLang);
    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", checkLang);
    };
  }, []);

  // Determine languageId and language param for API
  const languageId = (language === 'ma' || language === 'mr') ? 2 : 1;
  const langParam = languageId === 2 ? 'ma' : 'en';

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        // Fetch homepage video testimonials using languageId
        const url = `${myConstClass.NODE_API_BASE}/testimonials/homepage-playlist/videos?languageId=${languageId}`;
        addLog(`lang: ${language} | langParam: ${langParam} | languageId: ${languageId}`);
        addLog(`URL: ${url}`);
        const videoRes = await fetch(url);
        const videoData = await videoRes.json();
        if (videoData.success && Array.isArray(videoData.data)) {
          addLog(`videos count: ${videoData.data.length} | first: ${JSON.stringify(videoData.data[0])}`);
          setVideos(videoData.data);
          // console.log("FULL VIDEO DATA:", videoData.data);
          // console.log("FIRST VIDEO OBJECT:", videoData.data[0]);
        } else {
          setVideos([]);
        }

        // Fetch homepage written reviews using new API
        const adminReviewUrl = `${myConstClass.NODE_API_BASE}/reviews/admin/all?status=2&page=1&pageSize=50`;
        const adminReviewRes = await fetch(adminReviewUrl);
        const adminReviewData = await adminReviewRes.json();
        let homepageReviews = [];
        // Support both 'data' and 'reviews' keys for compatibility
        const reviewArr = Array.isArray(adminReviewData.data)
          ? adminReviewData.data
          : Array.isArray(adminReviewData.reviews)
            ? adminReviewData.reviews
            : [];
        if (reviewArr.length > 0) {
          homepageReviews = reviewArr.map((review) => ({
            reviewId: review.reviewId,
            author: review.name,
            text: review.description,
            location: review.address || "",
            createdOn: review.createdOn,
            sequence: review.sequence || 0
          }));
        }
        homepageReviews.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        setReviews(homepageReviews);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      }
    };
    fetchTestimonials();
  }, [language, languageId, langParam]);

  const featuredReview =
    reviews && reviews.length > 0 ? reviews[0] : null;

  return (
    <section
      className={`testimonials-section ${className}`.trim()}
      {...rest}
    >
      <div className="testimonials-container">

        <div className="testimonials-section__heading--main">
          <h2 className="videosection-title">
            {t('testimonials_video_title')}
          </h2>
        </div>

        <div className="testimonials-section__grid">

          <div className="testimonials-section__videos">

            {videos.length === 0 ? (
              <p className="testimonials-section__empty">
                Videos will be available soon.
              </p>
            ) : (
              <div className="testimonials-section__video-grid">
                {videos.map((video, index) => {
                  const name = video.participantName || video.participantNameMr || '';
                  // const description = video.description || video.descriptionMr || '';
                  const title = video.title || video.titleMr || '';
                  // Truncate description to 3 lines with ellipsis using inline style
                  return (
                    <VideoTestimonialCard
                      className="home-video-card"
                      key={video.testimonialId || index}
                      thumbnail={video.videoThumbnail || getYouTubeThumbnail(video.videoUrl)}
                      title={title}
                      onPlay={() => window.open(video.videoUrl, "_blank")}
                    />
                  );
                })}
              </div>
            )}

          </div>

          <div className="testimonials-section__reviews">
            <div className="testimonials-section__heading">
              <h2>{t('testimonials_written_title')}</h2>
            </div>

            <WrittenReviewCard
              review={featuredReview}
              reviews={reviews}
              eventId={null}
              userId={userId}
              hideWriteReview={true}
            />
          </div>

        </div>
      </div>
    </section>
  );
};

HomeTestimonialsSection.propTypes = {
  userId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  className: PropTypes.string,
};

export default HomeTestimonialsSection;